import { supabase } from "../supabase";
import { extractMentions } from "./mentions";
import { getBlockedUserIds } from "./blocks";

// Best-effort for most callers — a failed notification insert should never
// block the like/comment/follow action itself, which already succeeded on
// its own. Still returns {error} though: for a caller like Admin.jsx's
// warnUser, the notification IS the entire action, and it needs to know if
// this silently did nothing (e.g. rejected by RLS) instead of treating the
// report as handled when no warning ever actually went out.
// blockedIds lets a caller that already fetched the list (notifyMentions,
// looping over several mentioned users) pass it in instead of every call
// re-querying it — every other caller only ever calls this once per action
// and can leave it out, which fetches it here same as before.
export async function createNotification({
  userId,
  actorId,
  type,
  designId = null,
  commentText = null,
  blockedIds = null
}) {
  if (!userId || !actorId || userId === actorId) return { error: null };

  // Blocking is meant to cut off contact in both directions (see
  // utils/blocks.js) for peer-triggered notifications (like, comment,
  // download, follow, mention) — but not for "warning", an admin
  // moderation action that must reach the user regardless of any block
  // they've set up, or blocking the admin's account would be a way to
  // dodge being told about your own violation. Muting is deliberately not
  // checked here — per utils/mutes.js, a mute only affects what shows up
  // in the muter's own feed, not whether interaction (and its
  // notifications) still happens normally in either direction.
  if (type !== "warning") {
    const blocked = blockedIds || (await getBlockedUserIds(actorId));
    if (blocked.includes(userId)) {
      return { error: null };
    }
  }

  // Goes through a SECURITY DEFINER RPC rather than a direct insert — the
  // notifications table has an AFTER INSERT webhook trigger (sends a push
  // notification) that itself writes to a Supabase-internal bookkeeping
  // table the regular `authenticated` role has no access to. Run as the
  // caller, that write (and the whole insert with it) gets rejected with
  // "permission denied for table hooks" for every single notification;
  // running as the function's owner sidesteps that entirely.
  const { error } = await supabase.rpc("create_notification", {
    p_user_id: userId,
    p_type: type,
    p_design_id: designId,
    p_comment_text: commentText
  });

  if (error) {
    console.log("CREATE NOTIFICATION ERROR:", error);
  }

  return { error };
}

// Notifies every @mentioned user in a caption or comment — only called at
// creation time (a new post, a new comment), not on every caption edit, so
// re-saving an unchanged caption doesn't re-notify the same people.
export async function notifyMentions({
  text,
  actorId,
  designId = null,
  commentText = null,
  excludeUserId = null
}) {
  const usernames = extractMentions(text);
  if (usernames.length === 0 || !actorId) return;

  // Fetched once and passed to every createNotification call below —
  // block filtering happens inside createNotification itself now (it
  // covers every notification type, not just mentions), but without this
  // each mentioned user would trigger its own redundant blocked-ids fetch.
  const [{ data, error }, blockedIds] = await Promise.all([
    supabase.from("users").select("id").in("username", usernames),
    getBlockedUserIds(actorId)
  ]);

  if (error) {
    console.log("MENTION LOOKUP ERROR:", error);
    return;
  }

  // excludeUserId skips someone who's about to get a more specific
  // notification of their own for this same action — a reply's box is
  // pre-filled with "@theirusername " (Comments.jsx's startReply), and a
  // top-level comment can just as easily @mention the design's owner
  // outright, and both of those people already get a "someone
  // commented/replied" notification without also needing a "mentioned
  // you" one for the exact same comment.
  (data || [])
    .filter(u => u.id !== excludeUserId)
    .forEach(u => {
      createNotification({ userId: u.id, actorId, type: "mention", designId, commentText, blockedIds });
    });
}
